import * as React from "react";
import { ThemeProvider } from "@mui/material";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { createTickitMuiTheme, getPlatformColors } from "./tickitThemes";
import {
  resolveThemeMode,
  loadStoredThemePreference,
  saveThemePreference,
  canToggleTheme,
} from "./resolveThemeMode";
import { usesPlatformChrome } from "../platform/pageChrome";

export const ThemeModeContext = React.createContext({
  mode: "light",
  canToggle: false,
  toggleTheme: () => {},
  platformColors: getPlatformColors("light"),
});

function ThemeModeProviderInner({ children, role }) {
  const { pathname } = useLocation();
  const [preference, setPreference] = React.useState(loadStoredThemePreference);

  const mode = resolveThemeMode(pathname, role, preference);
  const canToggle = canToggleTheme(pathname, role);
  const muiTheme = React.useMemo(() => createTickitMuiTheme(mode), [mode]);
  const platformColors = React.useMemo(() => getPlatformColors(mode), [mode]);

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    document.documentElement.setAttribute(
      "data-tickit-chrome",
      usesPlatformChrome(pathname, role) ? "platform" : "marketing"
    );
  }, [mode, pathname, role]);

  const toggleTheme = React.useCallback(() => {
    if (!canToggle) return;
    setPreference((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      saveThemePreference(next);
      return next;
    });
  }, [canToggle]);

  const value = React.useMemo(
    () => ({ mode, canToggle, toggleTheme, platformColors }),
    [mode, canToggle, toggleTheme, platformColors]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

const mapStateToProps = (state) => ({
  role: state?.auth?.token?.split(" ")[0],
});

export default connect(mapStateToProps)(ThemeModeProviderInner);

export function useThemeMode() {
  return React.useContext(ThemeModeContext);
}
